import axios from 'axios';

// En producción, mismo origen (nginx proxifica /api); en local, el backend del .env
const BACKEND_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? process.env.REACT_APP_BACKEND_URL
  : window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Audio API
export const audioApi = {
  // Get feed with optional filters
  getFeed: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.color) params.append('color', filters.color);
    if (filters.shape) params.append('shape', filters.shape);
    if (filters.language) params.append('language', filters.language);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    
    const response = await api.get(`/audios/feed?${params.toString()}`);
    return response.data;
  },

  // Get single audio
  getAudio: async (audioId) => {
    const response = await api.get(`/audios/${audioId}`);
    return response.data;
  },

  // Upload audio
  uploadAudio: async (formData) => {
    const response = await api.post('/audios/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Play audio (increment count)
  playAudio: async (audioId) => {
    const response = await api.post(`/audios/${audioId}/play`);
    return response.data;
  },

  // Mark as useful
  markUseful: async (audioId) => {
    const response = await api.post(`/audios/${audioId}/useful`);
    return response.data;
  },

  // Toggle favorite
  toggleFavorite: async (audioId) => {
    const response = await api.post(`/audios/${audioId}/favorite`);
    return response.data;
  },

  // Get thread (hilo) with all its audios
  getThread: async (threadId) => {
    const response = await api.get(`/audios/thread/${threadId}`);
    return response.data;
  },

  // Create thread (hilo)
  createThread: async (data) => {
    const response = await api.post('/audios/thread', data);
    return response.data;
  },

  // Add audio to thread
  addToThread: async (threadId, audioId) => {
    const response = await api.post(`/audios/thread/${threadId}/add`, { audio_id: audioId });
    return response.data;
  },

  // Share audio
  shareAudio: async (audioId) => {
    const response = await api.post(`/audios/${audioId}/share`);
    return response.data;
  },

  // Get user favorites
  getFavorites: async (page = 0, limit = 20) => {
    const response = await api.get(`/audios/user/favorites?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Delete audio
  deleteAudio: async (audioId) => {
    const response = await api.delete(`/audios/${audioId}`);
    return response.data;
  },

  // Analyze audio with AI (transcribe + auto-classify)
  analyzeAudio: async (audioBlob, language = 'es') => {
    const formData = new FormData();
    formData.append('audio_file', audioBlob, 'audio.webm');
    formData.append('language', language);
    
    const response = await api.post('/audios/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Process voice command for search/filter
  analyzeVoiceCommand: async (audioBlob, language = 'es') => {
    const formData = new FormData();
    formData.append('audio_file', audioBlob, 'command.webm');
    formData.append('language', language);
    
    const response = await api.post('/audios/voice-command', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// Combo API
export const comboApi = {
  // Create combo
  create: async (data) => {
    const response = await api.post('/combos/', data);
    return response.data;
  },

  // Get public combos feed
  getFeed: async (page = 0, limit = 20, color = null) => {
    const response = await api.get(`/combos/feed?page=${page}&limit=${limit}${color ? `&color=${color}` : ''}`);
    return response.data;
  },

  // Get my combos
  getMyCombos: async () => {
    const response = await api.get('/combos/my');
    return response.data;
  },

  // Get combos created by a user
  getUserCombos: async (userId) => {
    const response = await api.get(`/combos/user/${userId}`);
    return response.data;
  },

  // Get saved combos
  getSavedCombos: async () => {
    const response = await api.get('/combos/saved');
    return response.data;
  },

  // Get single combo
  getCombo: async (comboId) => {
    const response = await api.get(`/combos/${comboId}`);
    return response.data;
  },

  // Update combo
  update: async (comboId, data) => {
    const response = await api.put(`/combos/${comboId}`, data);
    return response.data;
  },

  // Delete combo
  delete: async (comboId) => {
    const response = await api.delete(`/combos/${comboId}`);
    return response.data;
  },

  // Toggle save combo
  toggleSave: async (comboId) => {
    const response = await api.post(`/combos/${comboId}/save`);
    return response.data;
  },

  // Play combo
  play: async (comboId) => {
    const response = await api.post(`/combos/${comboId}/play`);
    return response.data;
  },

  // Get auto-generated audios for a combo
  getAutoAudios: async (comboId, limit = 10) => {
    const response = await api.get(`/combos/${comboId}/auto-audios?limit=${limit}`);
    return response.data;
  },

  // Share combo
  share: async (comboId) => {
    const response = await api.post(`/combos/${comboId}/share`);
    return response.data;
  }
};

// Follow API
export const followApi = {
  // Toggle follow/unfollow user
  toggle: async (userId) => {
    const response = await api.post(`/users/${userId}/follow`);
    return response.data;
  },

  // Check if following user
  isFollowing: async (userId) => {
    const response = await api.get(`/users/${userId}/is-following`);
    return response.data;
  },

  // Get users I'm following
  getFollowing: async () => {
    const response = await api.get('/users/following');
    return response.data;
  },

  // Get followers of a user
  getFollowers: async (userId) => {
    const response = await api.get(`/users/${userId}/followers`);
    return response.data;
  },

  // Get feed from following
  getFeed: async (limit = 50) => {
    const response = await api.get(`/feed/following?limit=${limit}`);
    return response.data;
  },

  // Get user profile
  getProfile: async (userId) => {
    const response = await api.get(`/users/${userId}/profile`);
    return response.data;
  },

  // Get top creators ranking
  getTopUsers: async (limit = 10) => {
    const response = await api.get(`/users/top?limit=${limit}`);
    return response.data;
  },

  // Update my profile (nickname, bio, socials, city)
  updateProfile: async (data) => {
    const response = await api.patch('/users/me', data);
    return response.data;
  }
};

// Report API
export const reportApi = {
  // Get report reasons
  getReasons: async () => {
    const response = await api.get('/report-reasons');
    return response.data;
  },

  // Create report
  create: async (audioId, reason, details = null) => {
    const response = await api.post('/reports/', { audio_id: audioId, reason, details });
    return response.data;
  },

  // Get pending reports (admin)
  getPending: async () => {
    const response = await api.get('/reports/pending');
    return response.data;
  }
};

// Push & Alarm API
export const alarmApi = {
  getVapidKey: async () => {
    const response = await api.get('/push/vapid-public-key');
    return response.data.public_key;
  },
  subscribePush: async (subscription) => {
    const response = await api.post('/push/subscribe', { subscription });
    return response.data;
  },
  setAlarm: async (comboId, time, days, comboName = null) => {
    const response = await api.post('/alarms/', {
      combo_id: comboId,
      time,
      days,
      tz_offset: new Date().getTimezoneOffset(),
      combo_name: comboName
    });
    return response.data;
  },
  getMyAlarms: async () => {
    const response = await api.get('/alarms/');
    return response.data;
  },
  deleteAlarm: async (comboId) => {
    const response = await api.delete(`/alarms/${comboId}`);
    return response.data;
  }
};

// Helper: VAPID key a Uint8Array para pushManager.subscribe
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

// Get audio file URL
export const getAudioFileUrl = (audioUrl) => {
  if (audioUrl.startsWith('http')) return audioUrl;
  return `${BACKEND_URL}${audioUrl}`;
};

export default api;
